import { isEmpty } from 'lodash-es';

import { Decision } from '@/shared/api/protocol-ts/model/dto_report_common_pb';
import { ConditionCode } from '@/shared/api/protocol-ts/model/dto_report_condition_codes_pb';
import { Condition } from '@/shared/api/protocol-ts/model/dto_report_condition_pb';

import {
  CONDITION_GROUP_BY_CODE,
  ONE_OF_CONDITION_GROUPS,
} from '../config/const';

export const conditionIsActive = (condition: Condition): boolean =>
  !isEmpty(condition) &&
  condition.Certainty?.EngineDecision === Decision.PositiveDecision;

// A one-of group (tooth type) is mutually exclusive: the engine reconciles exactly one
// member as current and negates the rest. The annotator's raw UserDecision/UserScore is
// left stale on a previously selected member after a switch, so one-of members defer to
// the engine's verdict; every other condition still honours the annotator's own choice.
const isMutuallyExclusiveCondition = (code: ConditionCode): boolean =>
  ONE_OF_CONDITION_GROUPS.includes(CONDITION_GROUP_BY_CODE[code]);

// FDA 2.0: annotators can add/score conditions the engine never predicted, so a condition
// is active on the engine's positive verdict OR the annotator's own decision/score.
export const conditionIsActiveForFDA = (condition: Condition): boolean => {
  if (isMutuallyExclusiveCondition(condition.Code)) {
    return conditionIsActive(condition);
  }

  if (isEmpty(condition)) {
    return false;
  }

  const certainty = condition.Certainty;

  // In FDA 2.0 the annotator's own decision is authoritative: it overrides the
  // engine verdict and any UserScore left over from a prior tooth approval.
  if (certainty?.UserDecision === Decision.PositiveDecision) {
    return true;
  }
  if (certainty?.UserDecision === Decision.NegativeDecision) {
    return false;
  }

  // No explicit annotator decision — the engine's positive verdict or a manual score.
  return (
    certainty?.EngineDecision === Decision.PositiveDecision ||
    certainty?.UserScore !== undefined
  );
};
